// components/EmployeeAbsenceTable.jsx
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Box,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';


const IMAGE_BASE_URL = "https://axopeople.blob.core.windows.net/images/";

const EmployeeAbsenceTable = ({
  employees = [],
  getLeaveTypeColor,
  getLeaveTypeIcon,
  formatLeaveType,
  leave=false,
  IMGemployees
}) => {
  const [selectedType, setSelectedType] = useState('ALL');

  // Safe format function to prevent errors
  const safeFormatLeaveType = (type) => {
    if (!type) return 'Unknown';
    return formatLeaveType(type);
  };

  // Filter employees based on selected type
  let filteredEmployees = selectedType === 'ALL'
    ? [...employees]
    : employees.filter(emp => emp.leaveType === selectedType);

  // ✅ SORT: by count DESC, then by days DESC
  filteredEmployees = filteredEmployees.sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return b.days - a.days;
  });

  // build this before rendering rows
  const employeeImageMap = React.useMemo(() => {
    if (!IMGemployees) return {};
    return IMGemployees.reduce((acc, emp) => {
      // build a key that matches how names appear in your `employees` prop
      const key = `${emp.firstName.trim().toLowerCase()} ${emp.lastName.trim().toLowerCase()}`;
      acc[key] = emp.image; // store only the image or whole emp if needed
      return acc;
    }, {});
  }, [IMGemployees]);


  const getHours = (emp) => {
    if (emp.leaveType === 'LATENESS' || emp.leaveType === 'PARTIAL_DAY') {
      return emp.hours || 0; // directly from record
    }
    if (emp.leaveType === 'FULL_DAY' || emp.leaveType === 'MULTIPLE_DAYS') {
      return (emp.days || 0) * 8; // 8h per day
    } else {
      //Leave types like UNPAID_LEAVE, SICK_LEAVE, etc.
      return (emp.days || 0) * 8; // 8h per day
    }
    return 0; // fallback
  };

  // Get unique leave types for filter buttons (filter out undefined/null values)
  const leaveTypes = [...new Set(employees.map(emp => emp.leaveType).filter(type => type))];

  // Export to Excel (high quality)
  const exportToExcel = () => {
    const worksheetData = filteredEmployees.map(emp => ({
      'Employee Name': `${emp.firstName} ${emp.lastName}`,
      'Records': emp.count,
      'Days': emp.days,
      'Leave Type': safeFormatLeaveType(emp.leaveType),
      'Image URL': emp.employee?.image ? `${IMAGE_BASE_URL}${emp.employee.image}` : 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Absence Records");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `Absence_Records_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {/* Filter Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button
          size="small"
          variant={selectedType === 'ALL' ? 'contained' : 'outlined'}
          onClick={() => setSelectedType('ALL')}
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            borderColor: selectedType === 'ALL' ? undefined : '#ccc',
          }}
        >
          {leave ? 'All Types' : 'All Periods'}
        </Button>
        {leaveTypes.map(type => (
          <Button
            key={type}
            size="small"
            variant={selectedType === type ? 'contained' : 'outlined'}
            onClick={() => setSelectedType(type)}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: selectedType === type ? getLeaveTypeColor(type) : 'transparent',
              color: selectedType === type ? '#fff' : getLeaveTypeColor(type),
              borderColor: getLeaveTypeColor(type),
              '&:hover': {
                backgroundColor: selectedType === type ? getLeaveTypeColor(type) : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            {getLeaveTypeIcon(type)} {safeFormatLeaveType(type)}
          </Button>
        ))}

        {/* Export Button */}
        <Button
          variant="contained"
          color="success"
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          sx={{ textTransform: 'none', fontWeight: 'bold' }}
        >
          Export to Excel
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell align="center"><strong>Records</strong></TableCell>
              <TableCell align="center"><strong>Days</strong></TableCell>
              <TableCell align="center"><strong>Hours</strong></TableCell>
              <TableCell align="center"><strong> {leave ? 'Type' : 'Period'}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="h6" color="success.main" sx={{ py: 3 }}>
                    Perfect Attendance! No absences recorded.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No records found for selected filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredEmployees.map((emp, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={
                          employeeImageMap[
                            `${emp.firstName.trim().toLowerCase()} ${emp.lastName.trim().toLowerCase()}`
                          ]
                          ? `${IMAGE_BASE_URL}${
                          employeeImageMap[
                          `${emp.firstName.trim().toLowerCase()} ${emp.lastName.trim().toLowerCase()}`
                          ]
                          }`
                        : undefined
                        }
                        sx={{ bgcolor: '#e0ececff', width: 40, height: 40 }}
                      >
                        {!employeeImageMap[
                          `${emp.firstName.trim().toLowerCase()} ${emp.lastName.trim().toLowerCase()}`
                        ] && <AccountCircle />}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {emp.firstName} {emp.lastName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={emp.count} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={`${emp.days}d`} size="small" color="secondary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${getHours(emp)}h`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title={safeFormatLeaveType(emp.leaveType)}>
                      <Chip
                        icon={<span>{getLeaveTypeIcon(emp.leaveType)}</span>}
                        label={safeFormatLeaveType(emp.leaveType)}
                        size="small"
                        sx={{
                          backgroundColor: getLeaveTypeColor(emp.leaveType),
                          color: '#fff',
                          fontWeight: 'bold',
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EmployeeAbsenceTable;

// // components/EmployeeAbsenceTable.jsx
// import React, { useState } from 'react';
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Avatar,
//   Chip,
//   Box,
//   Typography,
//   Button,
//   Tooltip,
// } from '@mui/material';
// import AccountCircle from '@mui/icons-material/AccountCircle';
// import FileDownloadIcon from '@mui/icons-material/FileDownload';
// import { saveAs } from 'file-saver';
// import * as XLSX from 'xlsx';

// const IMAGE_BASE_URL = "https://axopeople.blob.core.windows.net/images/";

// const EmployeeAbsenceTable = ({
//   employees = [],
//   getLeaveTypeColor,
//   getLeaveTypeIcon,
//   formatLeaveType,
// }) => {
//   const [selectedType, setSelectedType] = useState('ALL');

//   // Filter employees based on selected type
//   let filteredEmployees = selectedType === 'ALL'
//     ? [...employees]
//     : employees.filter(emp => emp.leaveType === selectedType);

//   // ✅ SORT: by count DESC, then by days DESC
//   filteredEmployees = filteredEmployees.sort((a, b) => {
//     if (b.count !== a.count) {
//       return b.count - a.count;
//     }
//     return b.days - a.days;
//   });

//   const getHours = (emp) => {
//     if (emp.leaveType === 'LATENESS' || emp.leaveType === 'PARTIAL_DAY') {
//       return emp.hours || 0; // directly from record
//     }
//     if (emp.leaveType === 'FULL_DAY' || emp.leaveType === 'MULTIPLE_DAYS') {
//       return (emp.days || 0) * 8; // 8h per day
//     }else{
//       //Leave types like UNPAID_LEAVE, SICK_LEAVE, etc.
//       return (emp.days || 0) * 8; // 8h per day
//     }
//       return 0; // fallback
// };


//   // Get unique leave types for filter buttons
//   const leaveTypes = [...new Set(employees.map(emp => emp.leaveType))];

//   // Export to Excel (high quality)
//   const exportToExcel = () => {
//     const worksheetData = filteredEmployees.map(emp => ({
//       'Employee Name': `${emp.firstName} ${emp.lastName}`,
//       'Records': emp.count,
//       'Days': emp.days,
//       'Leave Type': formatLeaveType(emp.leaveType),
//       'Image URL': emp.employee?.image ? `${IMAGE_BASE_URL}${emp.employee.image}` : 'N/A',
//     }));

//     const worksheet = XLSX.utils.json_to_sheet(worksheetData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "Absence Records");

//     const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
//     const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
//     saveAs(blob, `Absence_Records_${new Date().toISOString().slice(0,10)}.xlsx`);
//   };

  

//   return (
//     <Box sx={{ width: '100%', mt: 3 }}>
//       {/* Filter Buttons */}
//       <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
//         <Button
//           size="small"
//           variant={selectedType === 'ALL' ? 'contained' : 'outlined'}
//           onClick={() => setSelectedType('ALL')}
//           sx={{
//             textTransform: 'none',
//             fontWeight: 'bold',
//             borderColor: selectedType === 'ALL' ? undefined : '#ccc',
//           }}
//         >
//           All Periods
//         </Button>
//         {leaveTypes.map(type => (
//           <Button
//             key={type}
//             size="small"
//             variant={selectedType === type ? 'contained' : 'outlined'}
//             onClick={() => setSelectedType(type)}
//             sx={{
//               textTransform: 'none',
//               fontWeight: 'bold',
//               backgroundColor: selectedType === type ? getLeaveTypeColor(type) : 'transparent',
//               color: selectedType === type ? '#fff' : getLeaveTypeColor(type),
//               borderColor: getLeaveTypeColor(type),
//               '&:hover': {
//                 backgroundColor: selectedType === type ? getLeaveTypeColor(type) : 'rgba(0,0,0,0.04)',
//               },
//             }}
//           >
//             {getLeaveTypeIcon(type)} {formatLeaveType(type)}
//           </Button>
//         ))}

//         {/* Export Button */}
//         <Button
//           variant="contained"
//           color="success"
//           startIcon={<FileDownloadIcon />}
//           onClick={exportToExcel}
//           sx={{ textTransform: 'none', fontWeight: 'bold' }}
//         >
//           Export to Excel
//         </Button>
//       </Box>

//       {/* Table */}
//       <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
//         <Table>
//           <TableHead>
//             <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
//               <TableCell><strong>Employee</strong></TableCell>
//               <TableCell align="center"><strong>Records</strong></TableCell>
//               <TableCell align="center"><strong>Days</strong></TableCell>
//               <TableCell align="center"><strong>Hours</strong></TableCell>
//               <TableCell align="center"><strong>Period</strong></TableCell>
//               {/* <TableCell align="center"><strong>Type</strong></TableCell> */}
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {filteredEmployees.length === 0 ? (
//               <TableRow>
//                 <TableCell colSpan={4} align="center">
//                   <Typography variant="body2" color="textSecondary">
//                     No records found for selected filter.
//                   </Typography>
//                 </TableCell>
//               </TableRow>
//             ) : (
//               filteredEmployees.map((emp, index) => (
//                 <TableRow
//                   key={index}
//                   hover
//                   sx={{
//                     '&:hover': { backgroundColor: '#f9f9f9' },
//                     transition: 'background-color 0.2s',
//                   }}
//                 >
//                   <TableCell>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                       <Avatar
//                         src={emp.employee?.image ? `${IMAGE_BASE_URL}${emp.employee.image}` : undefined}
//                         sx={{ bgcolor: '#e0ececff', width: 40, height: 40 }}
//                       >
//                         {!emp.employee?.image && <AccountCircle />}
//                       </Avatar>
//                       <Typography variant="body2" sx={{ fontWeight: 500 }}>
//                         {emp.firstName} {emp.lastName}
//                       </Typography>
//                     </Box>
//                   </TableCell>
//                   <TableCell align="center">
//                     <Chip label={emp.count} size="small" color="primary" variant="outlined" />
//                   </TableCell>
//                   <TableCell align="center">
//                     <Chip label={`${emp.days}d`} size="small" color="secondary" variant="outlined" />
//                   </TableCell>
//                   <TableCell align="center">
//                     <Chip
//                       label={`${getHours(emp)}h`}
//                       size="small"
//                       color="warning"
//                       variant="outlined"
//                     />
//                   </TableCell>

//                   <TableCell align="center">
//                     <Tooltip title={formatLeaveType(emp.leaveType)}>
//                       <Chip
//                         icon={<span>{getLeaveTypeIcon(emp.leaveType)}</span>}
//                         label={formatLeaveType(emp.leaveType)}
//                         size="small"
//                         sx={{
//                           backgroundColor: getLeaveTypeColor(emp.leaveType),
//                           color: '#fff',
//                           fontWeight: 'bold',
//                         }}
//                       />
//                     </Tooltip>
//                   </TableCell>
//                 </TableRow>
//               ))
//             )}
//           </TableBody>
//         </Table>
//       </TableContainer>
//     </Box>
//   );
// };

// export default EmployeeAbsenceTable;

